import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { submitContact } from '../../lib/supabase';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await submitContact(formData);
      setSubmitMessage("Thanks! We'll be in touch within 24 hours.");
      setFormData({
        name: '',
        email: '',
        phone: '',
        business: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact submission error:', error);
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section className="relative overflow-hidden bg-white grid-bg pt-36 pb-20">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(13,148,136,0.14), transparent 58%), radial-gradient(ellipse at 85% 20%, rgba(249,115,22,0.10), transparent 55%)' }}
        ></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
            <div className="eyebrow">Get in Touch</div>
            <h1 className="mt-6 h-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em]">
              Let's Talk
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 font-body font-light max-w-2xl">
              Ready to dominate your Florida market with AI marketing? Get in touch.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-display font-bold text-navy-900 mb-6">
                Get Started Today
              </h2>
              <p className="text-base text-navy-600 mb-8 leading-relaxed">
                Whether you want a free AI Assessment, have questions about our services, or are ready to start winning the AI era, we're here to help.
              </p>

              <div className="space-y-6 mb-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy-900 mb-1 text-sm">Email Us</h3>
                    <a href="mailto:hello@floridaaimarketingexperts.com" className="text-teal-600 hover:text-teal-700 text-sm cursor-pointer">
                      hello@floridaaimarketingexperts.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy-900 mb-1 text-sm">Call or WhatsApp</h3>
                    <a href="tel:+19542964896" className="text-teal-600 hover:text-teal-700 text-sm cursor-pointer block">
                      (954) 296-4896
                    </a>
                    <a href="https://wa.me/19542964896" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 text-sm cursor-pointer inline-flex items-center gap-1 mt-1">
                      <i className="ri-whatsapp-line"></i> Message us on WhatsApp
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy-900 mb-1 text-sm">Location</h3>
                    <p className="text-navy-600 text-sm">
                      Serving all of Florida<br />
                      Miami • Tampa • Orlando • Jacksonville
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-navy-50 rounded-2xl p-6 border-2 border-navy-100">
                <h3 className="font-bold text-navy-900 mb-3 text-sm">Business Hours</h3>
                <div className="space-y-2 text-xs text-navy-700">
                  <div className="flex justify-between">
                    <span>Monday - Friday:</span>
                    <span className="font-semibold">9:00 AM - 6:00 PM EST</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday:</span>
                    <span className="font-semibold">10:00 AM - 4:00 PM EST</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday:</span>
                    <span className="font-semibold">Closed</span>
                  </div>
                </div>
                <p className="text-xs text-navy-600 mt-4">
                  * Emergency support available 24/7 for active clients
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-navy-100">
              <h2 className="text-2xl font-display font-bold text-navy-900 mb-6">
                Send Us a Message
              </h2>
              
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                <div className="mb-4">
                  <label htmlFor="name" className="block text-sm font-semibold text-navy-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-navy-200 rounded-lg focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 text-sm"
                    placeholder="John Smith"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-navy-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-navy-200 rounded-lg focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 text-sm"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-navy-700 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-navy-200 rounded-lg focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 text-sm"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="business" className="block text-sm font-semibold text-navy-700 mb-2">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    id="business"
                    name="business"
                    value={formData.business}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-navy-200 rounded-lg focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 text-sm"
                    placeholder="ABC Company"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-semibold text-navy-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-navy-200 rounded-lg focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 text-sm resize-none"
                    placeholder="Tell us about your business and what you're looking for..."
                  ></textarea>
                  <p className="text-xs text-navy-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                {submitMessage && (
                  <div className={`mb-6 p-4 rounded-lg ${submitMessage.includes('Thanks') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                    <p className="text-sm font-medium">{submitMessage}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary px-8 py-4 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
